<template>
  <div class="prompt-input">
    <VariableMentionEditor
      v-if="type === 'rich'"
      ref="controlRef"
      v-model="innerValue"
      class="prompt-input__control"
      v-bind="controlProps"
    />
    <u-input
      v-else
      ref="controlRef"
      v-model="innerValue"
      class="prompt-input__control"
      type="textarea"
      v-bind="controlProps"
    />

    <BotIcon
      v-if="showFullscreen"
      name="fullscreen"
      size="16"
      padding="4"
      class="prompt-input__expand"
      :data-testid="`open-fullscreen-${testIdSuffix}-button`"
      :tip="$t('components.PromptEditor.PromptInputWrapper.308759c198e2409c2b9c2813a126896c')"
      @click="handleOpen"
    />

    <FullscreenPromptEditor
      v-if="showFullscreen"
      v-model:visible="fullscreenVisible"
      :title="title"
      :value="innerValue"
      @cancel="handleFullscreenCancel"
    >
      <div class="prompt-input__fullscreen-control">
        <VariableMentionEditor
          v-if="type === 'rich'"
          v-model="innerValue"
          height="100%"
          v-bind="fullscreenProps"
        />
        <u-input
          v-else
          v-model="innerValue"
          type="textarea"
          v-bind="fullscreenProps"
        />
      </div>
    </FullscreenPromptEditor>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import VariableMentionEditor from '@C/VariableMentionEditor/index.vue';
import FullscreenPromptEditor from './FullscreenPromptEditor.vue';

// Props that only make sense inline (heights, counters, layout classes) are
// dropped when the control is rendered inside the fullscreen editor, otherwise
// they fight the 100% height fill of the modal body.
const INLINE_ONLY_PROPS = [
  'height',
  'minHeight',
  'maxHeight',
  'showLimit',
  'style',
  'class',
  'rows',
  'autosize',
  'autofocus'
];

// Editor methods callers rely on (refs to the inline control today).
const FORWARDED_METHODS = [
  'insertMentionByOption',
  'insertVariable',
  'setFocus',
  'focus',
  'blur',
  'clear',
  'getText'
];

export default defineComponent({
  name: 'PromptInputWrapper',
  components: { FullscreenPromptEditor, VariableMentionEditor },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    // rich: 统一提示词编辑器（支持 @ / & 触发候选），textarea: 普通文本域
    type: {
      type: String,
      default: 'rich',
      validator: value => ['rich', 'textarea'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    testIdSuffix: {
      type: String,
      default: 'prompt'
    },
    controlProps: {
      type: Object,
      default: () => ({})
    },
    disabledFullscreen: {
      type: Boolean,
      default: false
    },
    // 只有 prompt 类入口需要「全屏编辑」图标；参数值/条件值/SQL 等只换编辑器时置 false
    showFullscreen: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit, expose }) {
    const fullscreenVisible = ref(false);
    const controlRef = ref(null);

    const innerValue = computed({
      get: () => props.modelValue,
      set: value => emit('update:modelValue', value)
    });

    const fullscreenProps = computed(() => {
      const rest = { ...props.controlProps };
      INLINE_ONLY_PROPS.forEach(key => delete rest[key]);
      return rest;
    });

    const handleOpen = () => {
      if (props.disabledFullscreen) return;
      fullscreenVisible.value = true;
    };

    // 取消 in the modal restores the content captured when it was opened.
    const handleFullscreenCancel = value => {
      innerValue.value = typeof value === 'string' ? value : '';
    };

    // Callers used to hold a ref to the inline editor (e.g. insert a variable at
    // the caret), so forward the common editor methods to the inline control.
    const forwarded = {};
    FORWARDED_METHODS.forEach(name => {
      forwarded[name] = (...args) => controlRef.value?.[name]?.(...args);
    });
    expose(forwarded);

    return {
      controlRef,
      fullscreenProps,
      fullscreenVisible,
      handleFullscreenCancel,
      handleOpen,
      innerValue
    };
  }
});
</script>

<style lang="less" scoped>
@import '@/styles/mixins.less';

.prompt-input {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
}

// Fills the box when the caller gives the wrapper an explicit height.
.prompt-input__control {
  width: 100%;
  height: 100%;
  overflow: hidden;

  // uui renders textarea as .uui-input-wrap > textarea (+ word counter).
  :deep(.uui-input-wrap-textarea) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;
  }

  :deep(textarea),
  :deep(.ProseMirror),
  :deep(.tiptap) {
    .prompt-thin-scrollbar();
  }

  // Keep the text clear of the absolute fullscreen icon at the top-right.
  :deep(.tiptap) {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  :deep(textarea) {
    flex: 1;
    min-height: 0;
    max-height: 100%;
    padding-left: 16px !important;
    padding-right: 16px !important;
    overflow-y: auto !important;
    overscroll-behavior: contain;
  }

  :deep(.ProseMirror),
  :deep(.tiptap) {
    overscroll-behavior: contain;
  }
}

// Icon-only expand entry at the top-right of the input, matching the design.
.prompt-input__expand {
  position: absolute;
  top: 4px;
  right: 2px;
  z-index: 2;
  cursor: pointer;
  color: var(--text-neutral-color-text-tertiary);

  &:hover {
    color: var(--text-primary-color-primary-text);
  }
}

// The editor fills the modal body, so the plain textarea needs to fill too.
.prompt-input__fullscreen-control {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  // Rich editor: keep a definite height so .tiptap can scroll internally.
  :deep(.variable-mention-editor),
  :deep(.editor-shell) {
    height: 100%;
    min-height: 0;
  }

  // uui renders textarea as .uui-input-wrap > textarea (+ word counter).
  :deep(.uui-input-wrap-textarea) {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  :deep(textarea),
  :deep(.ProseMirror),
  :deep(.tiptap) {
    .prompt-thin-scrollbar();
  }

  :deep(textarea) {
    flex: 1;
    min-height: 0;
    height: auto;
    max-height: 100%;
    // uui leaves inline overflow/height behind when autosize is used, so the
    // clamp + scroll must win over it.
    overflow-y: auto !important;
    overscroll-behavior: contain;
    resize: none;
  }

  :deep(.ProseMirror),
  :deep(.tiptap) {
    overscroll-behavior: contain;
  }

  :deep(.uui-input-word-count) {
    flex: none;
  }
}
</style>
